// PaymentsMainPanel.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { TransactionList } from "./TransactionList";
import { BulkPaymentReminderDialog } from "./BulkPaymentReminderDialog";
import { BellRing, CalendarClock, Plus, Search, X } from "lucide-react";
import { TransactionData } from "@/types/types";
import { supabase } from "@/integrations/supabase/client";
import {
  findInvoiceInIndex,
  loadGymInvoiceIndexSafe,
  type GymInvoiceIndex,
} from "@/services/invoicesStorage";
import { useUserStore } from "@/store/userStore";
import { useDebounced } from "@/hooks/useDebounced";
import { downloadAndOpenPdfFromUrl } from "@/hooks/useMobileDownload";
import { cn } from "@/lib/utils";
import { usePaymentSummaryStore } from "@/store/api/paymentSummaryStore";
import {
  applyQuickFilter,
  buildCoverageBySub,
  computeKpis,
  filterBySearch,
  type QuickFilter,
} from "@/lib/payments/paymentsView";

interface PaymentsMainPanelProps {
  onOpenPaymentDialog: () => void;
  onOpenSplitPaymentDialog: () => void;
  // Accepte `null` : ouvrir l'échéancier depuis l'en-tête doit repartir sans
  // abonnement, et non réutiliser silencieusement la dernière ligne consultée.
  setSelectedTransaction: (transaction: TransactionData | null) => void;
  setIsInvoicePreviewOpen: (open: boolean) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isLoading: boolean;
}

export const PaymentsMainPanel: React.FC<PaymentsMainPanelProps> = ({
  onOpenPaymentDialog,
  onOpenSplitPaymentDialog,
  setSelectedTransaction,
  setIsInvoicePreviewOpen,
  searchQuery,
  setSearchQuery,
  isLoading,
}) => {
  const { t } = useTranslation("payments");
  const [quickFilter, setQuickFilter] = useState<QuickFilter>("");
  const [invoiceIndex, setInvoiceIndex] = useState<GymInvoiceIndex | null>(null);
  const gymId = useUserStore((state) => state.gymId);
  const role = useUserStore((state) => state.role);
  const [bulkReminderOpen, setBulkReminderOpen] = useState(false);
  const paymentsListRef = useRef<HTMLDivElement>(null);

  // 🔹 Données synthétiques par abonnement
  const { summaries, fetchSummaries, fetchPaymentsForSubscription } = usePaymentSummaryStore();

  // 1) Charger les factures + summaries au montage
  useEffect(() => {
    // Chaque salle a son propre dossier de factures : on ne charge que le sien
    // (plus le reliquat racine, pour retrouver les factures déjà émises).
    // La lecture est paginée : `list()` s'arrête sinon à 100 entrées et des
    // numéros déjà utilisés seraient réattribués.
    (async () => {
      setInvoiceIndex(await loadGymInvoiceIndexSafe(gymId));
    })();
    // `effective: true` remplissait les champs de base avec
    // `*_including_pending` : un paiement seulement saisi (pending) comptait
    // comme encaissé dans les KPI, le badge de statut et le filtre « En
    // retard ». On garde donc les colonnes « completed only » comme référence
    // d'encaissement, et on expose le pending à part.
    fetchSummaries({ effective: false, onlyActive: true });
     // charge v_subscription_payment_summary (active par défaut dans le store)
  }, [fetchSummaries, gymId]);

  // 2) KPIs (ignorer price=0 && totalPaid=0)
  const kpis = useMemo(() => computeKpis(summaries), [summaries]);

  // 3) coverageBySub (badges/infos dans la table)
  const coverageBySub = useMemo(() => buildCoverageBySub(summaries), [summaries]);

  // 4) Construire les "lignes" à partir des summaries (forme TransactionData)
  const baseRows: TransactionData[] = useMemo(() => {
    const toDateStr = (isoLike?: string | null) =>
      isoLike ? new Date(isoLike).toISOString().split("T")[0] : "";

    return summaries.map((s) => {
      // status d’affichage: paid/overpaid => completed, sinon pending
      const isOk = s.coverageState === "paid" || s.coverageState === "overpaid";
      const displayStatus = isOk ? "completed" : "pending";

      // date d’affichage: last_payment_date si dispo, sinon end_date, sinon start_date
      const primaryDate =
        s.lastPaymentDate ||
        (s.endDate ? `${s.endDate}T00:00:00.000Z` : s.startDate ? `${s.startDate}T00:00:00.000Z` : "");

      return {
        id: `sub-${s.subscriptionId}`,
        client: `${s.firstName ?? ""} ${s.lastName ?? ""}`.trim() || "—",
        amount: Number(s.subscriptionPrice || 0),
        date: toDateStr(primaryDate),
        status: displayStatus,
        paymentMethod: s.paymentMethod || "—",
        description: s.subscriptionType || "Subscription",
        subscriptionId: s.subscriptionId,
        userId: s.userId,
        subscriptionPrice: s.subscriptionPrice,
      } as TransactionData;
    });
  }, [summaries]);

  // 5) Filtre de recherche (multi-champs simple), débouncé pour ne pas
  // refiltrer la liste à chaque frappe
  const debouncedQuery = useDebounced(searchQuery, 250);
  const searchFiltered = useMemo(
    () => filterBySearch(baseRows, debouncedQuery),
    [baseRows, debouncedQuery]
  );

  // 6) Filtre “late” + filtres de couverture
  const rowsForList = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    return applyQuickFilter(searchFiltered, quickFilter, summaries, today);
  }, [searchFiltered, quickFilter, summaries]);

  // 7) Preview facture (clé basée sur l’id – souvent synthétique, fallback preview si pas trouvé)
  const handleViewInvoice = async (transaction: TransactionData) => {
    setSelectedTransaction(transaction);
    // Recherche sur la liste complète (chargée au montage), pas sur la première
    // page de 100 entrées : sinon une facture existante n'était pas retrouvée
    // et l'aperçu en régénérait une seconde.
    // Le rapprochement porte sur l'identifiant COMPLET de la ligne (`sub-<uuid>`),
    // avec repli sur l'ancienne convention tronquée à 8 caractères : deux
    // abonnements dont l'identifiant partage ce préfixe n'ouvrent plus la
    // facture l'un de l'autre (cf. invoicesStorage).
    const index = invoiceIndex ?? (await loadGymInvoiceIndexSafe(gymId));
    const existing = findInvoiceInIndex(index, transaction.id.toString());

    // Un administrateur ouvre l'aperçu : il peut y télécharger la facture ET la
    // corriger. Les autres profils récupèrent directement le PDF.
    if (existing && role !== "admin" && role !== "super_admin") {
      const { data: urlData } = await supabase.storage
        .from("invoices")
        .createSignedUrl(existing.path, 60 * 60);
      downloadAndOpenPdfFromUrl(urlData?.signedUrl, existing.file.name);
      return;
    }

    setIsInvoicePreviewOpen(true);
  };

  return (
    <>
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl md:text-3xl font-bold tracking-tight">
          {t("title")}
        </h1>
        <p className="text-xs md:text-sm text-muted-foreground">{t("overview")}</p>
      </div>

      {/* KPI cards — cliquables : chaque carte applique/retire le filtre
          correspondant. Rangée scrollable sur mobile, grille sur desktop. */}
      <div className="flex gap-2 overflow-x-auto pb-1 md:grid md:grid-cols-5 md:gap-3 md:overflow-visible md:pb-0 mb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {[
          { key: "unpaid" as const, label: t("kpi.unpaid", "Impayés"), value: kpis.unpaid, className: "bg-red-50 dark:bg-red-950" },
          { key: "partial" as const, label: t("kpi.partial", "Partiels"), value: kpis.partial, className: "bg-amber-50 dark:bg-amber-950" },
          { key: "paid" as const, label: t("kpi.paid", "Payés"), value: kpis.paid, className: "bg-emerald-50 dark:bg-emerald-950" },
          { key: "overpaid" as const, label: t("kpi.overpaid", "Surpayés"), value: kpis.overpaid, className: "bg-indigo-50 dark:bg-indigo-950" },
          // Compte à part : ces montants sont saisis mais pas encaissés, ils ne
          // doivent plus être additionnés aux « Payés ». Le filtre associé se
          // SUPERPOSE aux états de couverture (une ligne « payée » avec un
          // paiement en attente apparaît aussi ici).
          { key: "pending" as const, label: t("kpi.pending", "En attente"), value: kpis.pending, className: "bg-slate-50 dark:bg-slate-900" },
        ].map((k) => (
          <button
            key={k.label}
            type="button"
            aria-pressed={quickFilter === k.key}
            aria-label={t("filters.applyAria", { label: k.label, defaultValue: "Filtrer : {{label}}" })}
            onClick={() => setQuickFilter(quickFilter === k.key ? "" : k.key)}
            className={cn(
              "rounded-xl text-left min-w-[124px] shrink-0 md:min-w-0 transition-shadow cursor-pointer hover:shadow-md",
              k.className,
              quickFilter === k.key && "ring-2 ring-primary"
            )}
          >
            <div className="px-4 py-3 md:py-4">
              <div className="text-xs md:text-sm text-muted-foreground whitespace-nowrap">{k.label}</div>
              <div className="text-xl md:text-2xl font-bold tabular-nums">{k.value}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Filtres rapides — scroll horizontal sur mobile, wrap sur desktop */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-nowrap md:flex-wrap gap-2 mt-2 overflow-x-auto md:overflow-visible [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {[
            { key: "", label: t("filters.all", "Tous") },
            { key: "late", label: t("filters.late", "En retard") },
            { key: "unpaid", label: t("filters.unpaid", "Impayés") },
            { key: "partial", label: t("filters.partial", "Partiels") },
            { key: "paid", label: t("filters.paid", "Payés") },
            { key: "overpaid", label: t("filters.overpaid", "Surpayés") },
            { key: "pending", label: t("filters.pending", "En attente") },
          ].map((f) => (
            <Button
              key={f.key}
              variant={quickFilter === (f.key as any) ? "default" : "outline"}
              size="sm"
              className="h-9 shrink-0"
              onClick={() => setQuickFilter(f.key as any)}
            >
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Liste — marge basse élargie sur mobile pour que la pagination ne
          soit pas recouverte par le FAB */}
      <div ref={paymentsListRef}>
        <Card className="mb-24 md:mb-6">
          <CardHeader className="flex flex-row justify-between items-center gap-2 pb-2">
            <CardTitle className="text-base md:text-2xl">{t("transactions")}</CardTitle>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setBulkReminderOpen(true)}
                size="sm"
                className="h-9"
                disabled={!rowsForList.some((tx) => {
                  const coverage = tx.subscriptionId ? coverageBySub[tx.subscriptionId] : undefined;
                  return Boolean(tx.userId) && (coverage?.price ?? tx.amount) > (coverage?.totalPaid ?? 0);
                })}
              >
                <BellRing className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{t("bulkRemind.action")}</span>
                <span className="sr-only sm:hidden">{t("bulkRemind.action")}</span>
              </Button>
              {/* Échéancier : icône seule sur mobile (il était complètement
                  masqué < sm, accessible uniquement via le menu de ligne). */}
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedTransaction(null);
                  onOpenSplitPaymentDialog();
                }}
                size="sm"
                className="h-9"
                aria-label={t("splitPayment.title")}
              >
                <CalendarClock className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{t("splitPayment.title")}</span>
              </Button>
              {/* < md : l'action vit dans le FAB, pas de doublon ici */}
              <Button onClick={onOpenPaymentDialog} size="sm" className="h-9 hidden md:inline-flex">
                {t("newPayment")}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* text-base (16px) sur mobile : en dessous, iOS zoome le champ au focus */}
            <div className="flex items-center border rounded-md px-3 mb-4">
              <Search className="h-4 w-4 text-muted-foreground mr-2 shrink-0" />
              <Input
                type="search"
                placeholder={t("searchTransactions")}
                className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-10 text-base md:text-sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={() => setSearchQuery("")}
                  aria-label={t("clearSearch", { defaultValue: "Effacer la recherche" })}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <Separator className="my-3 sm:my-4" />
            <TransactionList
              transactions={rowsForList}
              onViewInvoice={handleViewInvoice}
              onSplitPayment={(tx) => {
                // On sélectionne la ligne avant d'ouvrir : sans abonnement
                // sélectionné, l'échéancier n'a rien sur quoi porter.
                setSelectedTransaction(tx);
                onOpenSplitPaymentDialog();
              }}
              isLoading={isLoading}
              coverageBySub={coverageBySub}
              fetchPaymentsForSubscription={fetchPaymentsForSubscription}
              onUpdatePayment={async (id, updates) => {
                const { updatePayment } = await import("@/services/edgeFunctionWrappers/paymentsManagementWrapper");
                const { getUserGymId } = await import("@/utils/gymAuth");
                const { data, error } = await updatePayment(id, getUserGymId(), updates);
                // `!error` ne suffit pas : le wrapper renvoie `{data:null,
                // error:null}` quand la session a expiré, ce qui annonçait un
                // succès pour une écriture jamais partie. On exige la ligne
                // mise à jour en retour.
                const succeeded = !error && data != null;
                if (succeeded) {
                  // Rafraîchir les summaries après mise à jour
                  fetchSummaries({ effective: false, onlyActive: true });
                }
                return succeeded;
              }}
              onDeletePayment={async (id) => {
                const { deletePayment } = await import("@/services/edgeFunctionWrappers/paymentsManagementWrapper");
                const { getUserGymId } = await import("@/utils/gymAuth");
                const { error } = await deletePayment(id, getUserGymId());
                if (!error) {
                  fetchSummaries({ effective: false, onlyActive: true });
                }
                return !error;
              }}
            />
          </CardContent>
        </Card>
      </div>

      {/* FAB mobile « Nouveau paiement » : la page admin n'a pas de bottom
          nav (AppLayout), le bouton du CardHeader est masqué < md. z-40 :
          sous le header mobile (z-50) et sous les sheets (z-[10000+]). */}
      <Button
        onClick={onOpenPaymentDialog}
        className="md:hidden fixed right-4 bottom-[calc(env(safe-area-inset-bottom)+1rem)] z-40 h-14 w-14 rounded-full shadow-lg"
        aria-label={t("newPayment")}
      >
        <Plus className="h-6 w-6" />
      </Button>

      <BulkPaymentReminderDialog
        open={bulkReminderOpen}
        onOpenChange={setBulkReminderOpen}
        transactions={rowsForList}
        coverageBySub={coverageBySub}
      />
    </>
  );
};
